import React, { useState, useEffect } from 'react';
import api from '../auth/api';

/**
 * CategoryManager Component
 * 
 * Manages categories and subcategories for exercises.
 * Allows therapists to create, edit, and delete categories with language support.
 */
const CategoryManager = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCategoryForm, setShowCategoryForm] = useState(false);
    const [showSubCategoryForm, setShowSubCategoryForm] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [selectedCategoryForSub, setSelectedCategoryForSub] = useState(null);
    const [languageFilter, setLanguageFilter] = useState('all');

    // Form states
    const [categoryForm, setCategoryForm] = useState({
        name: '',
        language: 'hi-IN',
        description: ''
    });

    const [subCategoryForm, setSubCategoryForm] = useState({
        name: '',
        description: ''
    });

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await api.get('/categories');
            setCategories(response.data);
            setError('');
        } catch (err) {
            console.error('Error fetching categories:', err);
            setError('Failed to load categories');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCategory = async (e) => {
        e.preventDefault();
        try {
            if (editingCategory) {
                await api.put(`/categories/${editingCategory.id}`, categoryForm);
            } else {
                await api.post('/categories', categoryForm);
            }
            await fetchCategories();
            resetCategoryForm();
        } catch (err) {
            console.error('Error saving category:', err);
            setError(err.response?.data?.detail || 'Failed to save category');
        }
    };

    const handleCreateSubCategory = async (e) => {
        e.preventDefault();
        try {
            await api.post(
                `/categories/${selectedCategoryForSub}/sub-categories`,
                subCategoryForm
            );
            await fetchCategories();
            resetSubCategoryForm();
        } catch (err) {
            console.error('Error creating subcategory:', err);
            setError(err.response?.data?.detail || 'Failed to create subcategory');
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        if (!window.confirm('Are you sure you want to delete this category? This will also delete all subcategories and exercises under it.')) {
            return;
        }

        try {
            await api.delete(`/categories/${categoryId}`);
            await fetchCategories();
        } catch (err) {
            console.error('Error deleting category:', err);
            setError(err.response?.data?.detail || 'Failed to delete category');
        }
    };

    const handleDeleteSubCategory = async (subCategoryId) => {
        if (!window.confirm('Are you sure you want to delete this subcategory? This will also delete all exercises under it.')) {
            return;
        }

        try {
            await api.delete(`/sub-categories/${subCategoryId}`);
            await fetchCategories();
        } catch (err) {
            console.error('Error deleting subcategory:', err);
            setError(err.response?.data?.detail || 'Failed to delete subcategory');
        }
    };

    const resetCategoryForm = () => {
        setCategoryForm({ name: '', language: 'hi-IN', description: '' });
        setEditingCategory(null);
        setShowCategoryForm(false);
    };

    const resetSubCategoryForm = () => {
        setSubCategoryForm({ name: '', description: '' });
        setSelectedCategoryForSub(null);
        setShowSubCategoryForm(false);
    };

    const startEditCategory = (category) => {
        setEditingCategory(category);
        setCategoryForm({
            name: category.name,
            language: category.language,
            description: category.description || ''
        });
        setShowCategoryForm(true);
    };

    const filteredCategories = languageFilter === 'all'
        ? categories
        : categories.filter(cat => cat.language === languageFilter);

    if (loading) {
        return <div className="loading">Loading categories...</div>;
    }

    return (
        <div className="category-manager">
            <div className="category-header">
                <h2>Category Management</h2>
                <div className="category-actions">
                    <select
                        value={languageFilter}
                        onChange={(e) => setLanguageFilter(e.target.value)}
                        className="language-filter"
                    >
                        <option value="all">All Languages</option>
                        <option value="hi-IN">Hindi</option>
                        <option value="en-US">English</option>
                    </select>
                    <button
                        onClick={() => setShowCategoryForm(true)}
                        className="btn btn-primary"
                    >
                        + New Category
                    </button>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {/* Category Form Modal */}
            {showCategoryForm && (
                <div className="modal-overlay" onClick={resetCategoryForm}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>{editingCategory ? 'Edit Category' : 'Create New Category'}</h3>
                        <form onSubmit={handleCreateCategory}>
                            <div className="form-group">
                                <label>Name *</label>
                                <input
                                    type="text"
                                    value={categoryForm.name}
                                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                                    required
                                    maxLength={100}
                                    placeholder="e.g., Phonetic-based"
                                />
                            </div>

                            <div className="form-group">
                                <label>Language *</label>
                                <select
                                    value={categoryForm.language}
                                    onChange={(e) => setCategoryForm({ ...categoryForm, language: e.target.value })}
                                    required
                                >
                                    <option value="hi-IN">Hindi</option>
                                    <option value="en-US">English</option>
                                    <option value="both">Both</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={categoryForm.description}
                                    onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                                    maxLength={500}
                                    rows={3}
                                    placeholder="Optional description..."
                                />
                            </div>

                            <div className="form-actions">
                                <button type="button" onClick={resetCategoryForm} className="btn btn-secondary">
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingCategory ? 'Update' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* SubCategory Form Modal */}
            {showSubCategoryForm && (
                <div className="modal-overlay" onClick={resetSubCategoryForm}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Create New SubCategory</h3>
                        <form onSubmit={handleCreateSubCategory}>
                            <div className="form-group">
                                <label>Name *</label>
                                <input
                                    type="text"
                                    value={subCategoryForm.name}
                                    onChange={(e) => setSubCategoryForm({ ...subCategoryForm, name: e.target.value })}
                                    required
                                    maxLength={100}
                                    placeholder="e.g., Initial Consonants"
                                />
                            </div>

                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={subCategoryForm.description}
                                    onChange={(e) => setSubCategoryForm({ ...subCategoryForm, description: e.target.value })}
                                    maxLength={500}
                                    rows={3}
                                    placeholder="Optional description..."
                                />
                            </div>

                            <div className="form-actions">
                                <button type="button" onClick={resetSubCategoryForm} className="btn btn-secondary">
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Create
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Categories List */}
            <div className="categories-list">
                {filteredCategories.length === 0 ? (
                    <div className="empty-state">
                        <p>No categories found. Create one to get started!</p>
                    </div>
                ) : (
                    filteredCategories.map(category => (
                        <div key={category.id} className="category-card">
                            <div className="category-header-row">
                                <div className="category-info">
                                    <h3>{category.name}</h3>
                                    <span className="language-badge">{category.language}</span>
                                </div>
                                <div className="category-actions-row">
                                    <button
                                        onClick={() => startEditCategory(category)}
                                        className="btn btn-sm btn-secondary"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSelectedCategoryForSub(category.id);
                                            setShowSubCategoryForm(true);
                                        }}
                                        className="btn btn-sm btn-primary"
                                    >
                                        + SubCategory
                                    </button>
                                    <button
                                        onClick={() => handleDeleteCategory(category.id)}
                                        className="btn btn-sm btn-danger"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>

                            {category.description && (
                                <p className="category-description">{category.description}</p>
                            )}

                            {/* SubCategories */}
                            {category.sub_categories && category.sub_categories.length > 0 && (
                                <div className="subcategories-list">
                                    <h4>SubCategories:</h4>
                                    {category.sub_categories.map(subCat => (
                                        <div key={subCat.id} className="subcategory-item">
                                            <div className="subcategory-info">
                                                <strong>{subCat.name}</strong>
                                                {subCat.description && <p>{subCat.description}</p>}
                                            </div>
                                            <button
                                                onClick={() => handleDeleteSubCategory(subCat.id)}
                                                className="btn btn-sm btn-danger"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default CategoryManager;
